import { useMemo } from 'react'
import { DjayAccessory, DjayTier } from './DjayAvatar'
import {
  ACHIEVEMENTS,
  AchievementCheckData,
} from '../components/progress/AchievementsSection'
import { MODULES } from '../content'
import { VariantProgress } from './useVariantUnlocks'

export interface AccessoryUnlockInfo {
  id: DjayAccessory
  name: string
  unlocked: boolean
  condition: string
  progress: VariantProgress
}

export interface TierUnlockInfo {
  id: DjayTier
  name: string
  unlocked: boolean
  condition: string
  progress: VariantProgress
}

// Map accessory IDs to human-readable names
const ACCESSORY_NAMES: Record<DjayAccessory, string> = {
  none: 'Geen',
  seedling: 'Sprietje',
  pencil: 'Potlood',
  badge100: '100 Badge',
  smallFlame: 'Vlammetje',
  trophy: 'Trofee',
  doubleFlameCrown: 'Vuurkroon',
  starWand: 'Sterrenstaf',
  tripleStars: 'Drie Sterren',
  scepter: 'Scepter',
  leafGarland: 'Lauwerkrans',
  medal: 'Medaille',
  cosmicOrb: 'Kosmische Bol',
  shadowOrb: 'Schaduw Bol',
}

// Map tier IDs to human-readable names
const TIER_NAMES: Record<DjayTier, string> = {
  rookie: 'Beginner',
  pro: 'Pro',
  master: 'Meester',
}

export function useDjayCustomization(checkData: AchievementCheckData) {
  return useMemo(() => {
    // 1. Accessories
    const accessoryMap = new Map<DjayAccessory, AccessoryUnlockInfo>()

    // Always add 'none'
    accessoryMap.set('none', {
      id: 'none',
      name: ACCESSORY_NAMES.none,
      unlocked: true,
      condition: 'Altijd beschikbaar',
      progress: { current: 1, max: 1, label: 'Ontgrendeld' },
    })

    // Map from achievements
    ACHIEVEMENTS.forEach((achievement) => {
      if (achievement.accessory && achievement.accessory !== 'none') {
        const isUnlocked = achievement.check(checkData)
        const progress = achievement.getProgress
          ? achievement.getProgress(checkData)
          : { current: isUnlocked ? 1 : 0, max: 1, label: 'Ontgrendeld' }

        // If multiple achievements grant the same accessory, keep the one that is unlocked, or the first one
        const existing = accessoryMap.get(achievement.accessory)
        if (!existing || (!existing.unlocked && isUnlocked)) {
          accessoryMap.set(achievement.accessory, {
            id: achievement.accessory,
            name:
              ACCESSORY_NAMES[achievement.accessory] || achievement.accessory,
            unlocked: isUnlocked,
            condition: achievement.description,
            progress,
          })
        }
      }
    })

    const accessories = Array.from(accessoryMap.values())

    // 2. Tiers
    const tiers: TierUnlockInfo[] = [
      {
        id: 'rookie',
        name: TIER_NAMES.rookie,
        unlocked: true,
        condition: 'Altijd beschikbaar',
        progress: { current: 1, max: 1, label: 'Ontgrendeld' },
      },
      {
        id: 'pro',
        name: TIER_NAMES.pro,
        unlocked: checkData.masteredCount >= 1 && checkData.level >= 5,
        condition: 'Level 5 bereikt & 1 vak afgerond',
        progress: {
          current:
            (checkData.level >= 5 ? 1 : 0) +
            (checkData.masteredCount >= 1 ? 1 : 0),
          max: 2,
          label: 'doelen',
        },
      },
      {
        id: 'master',
        name: TIER_NAMES.master,
        unlocked:
          checkData.masteredCount >= MODULES.length && checkData.level >= 15,
        condition: `Level 15 bereikt & alle ${MODULES.length} vakken afgerond`,
        progress: {
          current:
            (checkData.level >= 15 ? 1 : 0) +
            (checkData.masteredCount >= MODULES.length ? 1 : 0),
          max: 2,
          label: 'doelen',
        },
      },
    ]

    return {
      accessories,
      tiers,
      unlockedAccessoriesCount: accessories.filter((a) => a.unlocked).length,
      unlockedTiersCount: tiers.filter((t) => t.unlocked).length,
    }
  }, [checkData])
}
